/**
 *  @class
 *  @function FreeShipping
 *  The rewards bar (snippets/free-shipping.liquid), in the drawer and on the
 *  cart page. Every cart redraw brings a new one.
 *
 *  In the shop's currency the snippet has drawn the state (its inline
 *  --percentage says so). In any other currency the minimums, kept in the
 *  shop's cents, are converted with Shopify.currency.rate and the state is
 *  drawn here. Amounts use the shop's plain money format (no currency
 *  code), as the snippet prints them: with tiers, a whole amount loses its
 *  zero decimals (money_without_trailing_zeros), and the amounts it printed
 *  stay in the shop's currency. With one tier the remaining amount is
 *  always written here with its decimals ("$39.00"), as the bar always did.
 *
 *  One tier: the free-shipping--complete class shows the free shipping
 *  message instead of the remaining amount. Tiers (data-tiers): the message
 *  of the next tier, or the all-unlocked one, the reached markers and the
 *  gift buttons of the reached tiers. The bar fills against the highest
 *  minimum.
 *
 *  Add gift: theme.cart.add, then render (focus on the new line), then
 *  added. A refused add shows its message in the bar's alert and changes
 *  nothing else.
 */
if (!customElements.get('free-shipping')) {
  class FreeShipping extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onClick.bind(this));
    }

    connectedCallback() {
      this.drawn = this.style.getPropertyValue('--percentage') !== '';
      this.update();
    }

    // The shop's plain money format (theme.settings.money_with_currency_format
    // is shop.money_format); `trim` drops a whole amount's zero decimals, as
    // money_without_trailing_zeros does.
    money(cents, trim) {
      const format = theme.settings.money_with_currency_format || '${{amount}}';
      const text = theme.money ? theme.money(cents, format) : formatMoney(cents, format);
      return trim && cents % 100 === 0 ? text.replace(/[.,]00(?=\D*$)/, '') : text;
    }

    update() {
      const total = parseInt(this.dataset.cartTotal, 10) || 0;
      const rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
      const convert = (cents) => Math.round((parseInt(cents, 10) || 0) * rate);
      let tiers = null;
      try {
        tiers = this.dataset.tiers ? JSON.parse(this.dataset.tiers) : null;
      } catch (e) {
        tiers = null;
      }
      if (!Array.isArray(tiers) || !tiers.length) tiers = [{ minimum: this.dataset.minimum }];
      const minimums = tiers.map((tier) => convert(tier.minimum));
      const highest = Math.max(...minimums);
      const next = minimums.findIndex((minimum) => total < minimum);
      const percentage = highest > 0 ? Math.min(total / highest, 1) : 1;

      this.classList.toggle('free-shipping--complete', next === -1);

      if (this.dataset.tiers) {
        this.querySelectorAll('.free-shipping--message').forEach((message) => {
          const index = parseInt(message.dataset.tier, 10);
          message.hidden = index !== next;
          const amount = message.querySelector('span');
          if (!this.drawn && index === next && amount) amount.textContent = this.money(minimums[index] - total, true);
        });
        const full = this.querySelector('.free-shipping--text-full');
        if (full) full.hidden = next !== -1;
        this.querySelectorAll('.free-shipping--marker').forEach((marker) => {
          const reached = total >= minimums[parseInt(marker.dataset.tier, 10)];
          marker.classList.toggle('free-shipping--reached', reached);
          if (marker.firstElementChild) marker.firstElementChild.hidden = !reached;
        });
        this.querySelectorAll('.free-shipping--gift').forEach((button) => {
          button.hidden = total < minimums[parseInt(button.dataset.tier, 10)];
        });
      } else if (next === 0) {
        const amount = this.querySelector('.free-shipping--text span');
        if (amount) amount.textContent = this.money(minimums[0] - total, false);
      }

      if (!this.drawn) this.style.setProperty('--percentage', percentage);
    }

    onClick(event) {
      const button = event.target.closest('.free-shipping--gift');
      if (!button || !this.contains(button) || this.busy) return;
      const requested = [{ id: parseInt(button.dataset.variantId, 10), quantity: 1 }];
      const context = this.closest('#Cart-Drawer') ? 'dialog' : 'cart';

      // Not disabled: the button keeps keyboard focus until the redraw.
      this.busy = true;
      button.setAttribute('aria-disabled', 'true');
      this.showError('');

      theme.cart.add(requested)
        .then((state) => {
          const line = (state.items || [])[0];
          theme.cart.render(state, { focusKey: line && line.key });
          theme.cart.added(requested, state, context);
        }, (error) => {
          this.showError((error.body && error.body.description) || theme.strings.cartError);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.busy = false;
          button.removeAttribute('aria-disabled');
        });
    }

    showError(message) {
      const slot = this.querySelector('.free-shipping--error');
      if (!slot) return;
      slot.querySelector('.free-shipping--error-text').textContent = message;
      slot.hidden = !message;
    }
  }

  customElements.define('free-shipping', FreeShipping);
}
